import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../lib/api';
import { MagnifyingGlass, User, Calendar, PhoneCall, EnvelopeSimple, Notebook } from '@phosphor-icons/react';

const TYPE_ICONS = { call: PhoneCall, email: EnvelopeSimple, meeting: Calendar, note: Notebook, todo: Calendar };

export default function SearchPage() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const [q, setQ] = useState(params.get('q') || '');
  const [results, setResults] = useState({ contacts: [], activities: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const input = useRef();

  useEffect(() => { input.current?.focus(); }, []);

  const run = async (query) => {
    if (!query || !query.trim()) { setResults({ contacts: [], activities: [], total: 0 }); return; }
    setLoading(true);
    try {
      const { data } = await api.get('/search/keyword', { params: { q: query.trim() } });
      setResults(data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    const t = setTimeout(() => { run(q); if (q) setParams({ q }); else setParams({}); }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const highlight = (text, needle) => {
    if (!text || !needle) return text || '';
    const idx = text.toLowerCase().indexOf(needle.toLowerCase());
    if (idx === -1) return text;
    return (
      <>{text.slice(0, idx)}<mark className="bg-[#F39C12] text-white px-0.5">{text.slice(idx, idx + needle.length)}</mark>{text.slice(idx + needle.length)}</>
    );
  };

  return (
    <div className="flex flex-col h-full bg-[#E8E8E8]" data-testid="search-page">
      <div className="bg-gradient-to-b from-[#F39C12] to-[#D68910] px-4 py-2 border-b border-[#999] shrink-0">
        <h1 className="text-[14px] font-bold text-white flex items-center gap-2"><MagnifyingGlass size={16} weight="bold" />Keyword Search</h1>
      </div>

      <div className="p-4 bg-white border-b border-[#CCC] shrink-0">
        <div className="max-w-3xl mx-auto flex items-center gap-2">
          <MagnifyingGlass size={18} className="text-[#666]" />
          <input
            ref={input}
            value={q}
            onChange={e => setQ(e.target.value)}
            placeholder="Search contacts, notes, and activities..."
            className="flex-1 h-[36px] px-3 text-[14px] border border-[#B0B0B0] focus:outline-none focus:border-[#0B5394]"
            data-testid="search-input"
          />
          {loading && <span className="text-[12px] text-[#666]">Searching...</span>}
          {!loading && q && <span className="text-[12px] text-[#666]" data-testid="search-count">{results.total} results</span>}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4">
        <div className="max-w-3xl mx-auto space-y-4">
          {results.contacts.length > 0 && (
            <section className="bg-white border border-[#CCC]" data-testid="search-contacts-section">
              <div className="px-3 py-2 bg-[#0B5394] text-white text-[12px] font-bold flex items-center gap-2">
                <User size={14} weight="bold" /> Contacts ({results.contacts.length})
              </div>
              <ul>
                {results.contacts.map(c => (
                  <li key={c.contact_id} className="border-b border-[#EEE]">
                    <button
                      onClick={() => navigate(`/contact?id=${c.contact_id}`)}
                      className="w-full text-left px-3 py-2 hover:bg-[#F0F4FB] transition-colors"
                      data-testid={`search-contact-${c.contact_id}`}
                    >
                      <div className="text-[13px] font-semibold text-[#0B5394]">{highlight(`${c.first_name} ${c.last_name}`, q)}</div>
                      <div className="text-[11px] text-[#555]">{highlight(c.company || '', q)} {c.email && <span className="text-[#777]"> · {highlight(c.email, q)}</span>}</div>
                      {c.notes && <div className="text-[11px] text-[#666] mt-1 line-clamp-2">{highlight(c.notes, q)}</div>}
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {results.activities.length > 0 && (
            <section className="bg-white border border-[#CCC]" data-testid="search-activities-section">
              <div className="px-3 py-2 bg-[#0B5394] text-white text-[12px] font-bold flex items-center gap-2">
                <Calendar size={14} weight="bold" /> Activities & Notes ({results.activities.length})
              </div>
              <ul>
                {results.activities.map(a => {
                  const Icon = TYPE_ICONS[a.activity_type] || Notebook;
                  return (
                    <li key={a.activity_id} className="border-b border-[#EEE]">
                      <button
                        onClick={() => navigate(`/contact?id=${a.contact_id}`)}
                        className="w-full text-left px-3 py-2 hover:bg-[#F0F4FB] transition-colors"
                        data-testid={`search-activity-${a.activity_id}`}
                      >
                        <div className="text-[12px] font-semibold text-[#333] flex items-center gap-2">
                          <Icon size={12} weight="bold" className="text-[#0B5394]" />
                          {highlight(a.title, q)}
                          <span className="text-[10px] text-[#999] ml-auto">{a.scheduled_date || a.created_at?.slice(0, 10)}</span>
                        </div>
                        <div className="text-[10px] text-[#777] mt-0.5">with <span className="text-[#0B5394] font-medium">{a.contact_name}</span></div>
                        {a.content && <div className="text-[11px] text-[#666] mt-1 line-clamp-2">{highlight(a.content, q)}</div>}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </section>
          )}

          {!loading && q && results.total === 0 && (
            <div className="bg-white border border-[#CCC] p-8 text-center text-[#666] text-[13px]" data-testid="search-empty">
              No matches for "{q}"
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
